import axios from 'axios'
import { ElMessage } from 'element-plus'
import { isCacheable, makeCacheKey, getCache, setCache, invalidateResource, cacheDebugLog } from '@/utils/requestCache'

// 创建 axios 实例
const request = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '/',
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' }
});

// 请求拦截器
request.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // 查询缓存：命中且未过期 → 直接用本地缓存响应，不再请求服务器（省带宽）
    if (isCacheable(config)) {
      const key = makeCacheKey(config.url, config.params)
      const cached = getCache(key)
      if (cached !== undefined) {
        config._fromCache = true
        cacheDebugLog('HIT', (config.method || 'get').toUpperCase() + ' ' + key)
        config.adapter = () => Promise.resolve({
          data: cached,
          status: 200,
          statusText: 'OK (from cache)',
          headers: {},
          config
        })
      } else {
        cacheDebugLog('MISS', (config.method || 'get').toUpperCase() + ' ' + key)
      }
      config._cacheKey = key
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  response => {
    const res = response.data
    // 后端统一返回格式: { code, message, data }
    if (res.code !== undefined && res.code !== 200) {
      // 业务错误
      return Promise.reject(new Error(res.message || '请求失败'))
    }
    const cfg = response.config || {}
    // 本次为真实请求的可缓存查询 → 写入缓存（缓存命中的不再重复写）
    if (cfg._cacheKey && !cfg._fromCache) {
      setCache(cfg._cacheKey, res)
      cacheDebugLog('WRITE', cfg._cacheKey)
    }
    // 增/删/改成功后 → 失效该资源相关缓存，下次查询重写为最新数据
    if ((cfg.method || 'get').toLowerCase() !== 'get') {
      invalidateResource(cfg.url)
      cacheDebugLog('INVALIDATE', (cfg.method || '').toUpperCase() + ' ' + cfg.url)
    }
    // 直接返回完整响应（包含 code, message, data）
    return res
  },
  error => {
    // 处理 HTTP 错误
    if (error.response) {
      const status = error.response.status
      const data = error.response.data
      
      switch (status) {
        case 401:
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          localStorage.removeItem('isLoggedIn')
          localStorage.removeItem('role')
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
          break
        case 403:
          ElMessage.error('无权限访问')
          break
        case 404:
          ElMessage.error('接口不存在')
          break
        case 500:
          ElMessage.error(data?.message || '服务器错误')
          break
        default:
          ElMessage.error(data?.message || '请求失败')
      }
    } else if (error.message) {
      ElMessage.error(error.message)
    }
    return Promise.reject(error)
  }
)

export default request